import { useState, useEffect } from "react"; // useCallback — re-add when uncommenting gallery

/*
╔══════════════════════════════════════════════════════════════════╗
║                                                                  ║
║   CROYDON INCLUSIVE CHOIR — Website                              ║
║                                                                  ║
║   HOW THIS FILE IS ORGANISED:                                    ║
║                                                                  ║
║   1. CONFIG          — URLs, passwords, settings                 ║
║   2. GALLERY DATA    — Albums and photos                         ║
║   3. DOCUMENTS       — Downloadable member docs                  ║
║   4. UPCOMING DATES  — Manual diary entries                      ║
║   5. FAQ             — Questions and answers                     ║
║   6. WHAT TO EXPECT  — Rehearsal info                            ║
║   7. STYLES          — All CSS                                   ║
║   8. COMPONENTS      — Reusable bits (Nav, Footer, etc.)         ║
║   9. PAGES           — Home, Dates, Gallery, Members,            ║
║                        What to Expect, Contact/Newsletter         ║
║  10. APP             — Main app with page routing                ║
║                                                                  ║
║   To find a section quickly, search for ═══ SECTION NAME         ║
║                                                                  ║
╚══════════════════════════════════════════════════════════════════╝
*/


/* ═══════════════════════════════════════════════════════════════
   1. CONFIG
   Update these values to connect your integrations.
   ═══════════════════════════════════════════════════════════════ */

const CONFIG = {

  // ── Google Sheets (Announcements) ──────────────────────────
  // 1. Create a Google Sheet with columns: Date, Title, Content, Pinned
  // 2. File → Share → Publish to web → select CSV → Publish
  // 3. Paste the URL below
  ANNOUNCEMENTS_CSV_URL: "https://docs.google.com/spreadsheets/d/e/2PACX-1vRLQpQwp-JyjXhgaM3JgDhuZmHA0IepBYjxOX07WD8xM5MxLiKA7DCtj-RpYmKxIdMXuz0tdcWUrOcz/pub?gid=0&single=true&output=csv",

  // ── Google Calendar ────────────────────────────────────────
  // The calendar ID (your gmail) — used to fetch events via API
  GOOGLE_CALENDAR_ID: "croydoninclusivechoir@gmail.com",

  // Google Calendar API key
  GOOGLE_CALENDAR_API_KEY: "AIzaSyAMLlrp563TQmymnl0RiD_T5g-SUvLByro",

  // ── Calendar Subscribe Links (auto-generated from calendar ID) ──
  // These let people add your calendar to their own
  get CALENDAR_GOOGLE_URL() { return `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(this.GOOGLE_CALENDAR_ID)}`; },
  get CALENDAR_ICAL_URL() { return `https://calendar.google.com/calendar/ical/${encodeURIComponent(this.GOOGLE_CALENDAR_ID)}/public/basic.ics`; },

  // ── Formspree ──────────────────────────────────────────────
  // Create forms at formspree.io and paste the endpoint URLs
  FORMSPREE_NEWSLETTER: "https://formspree.io/f/mgolnqoe",
  FORMSPREE_CONTACT: "https://formspree.io/f/xpqjynww",

  // ── Membership Form (Google Form) ──────────────────────────
  MEMBERSHIP_FORM:
    "https://docs.google.com/forms/d/e/1FAIpQLSf9Ksgs_22vpTkWfC0bCYHoU6_6aDSXzqDA92LO12OgWdM0WA/viewform?embedded=true",

  // ── Members Area Password ──────────────────────────────────
  MEMBERS_PASSWORD: "choir2026",

  // ── Google Drive Folder ────────────────────────────────────
  GDRIVE_FOLDER: "https://drive.google.com/drive/folders/1gpfWp50gEwOrKum4Cku7XKtERBbyOnl6?usp=sharing",

  // ── Contact & Social ───────────────────────────────────────
  EMAIL: "croydonlgbtqchoir@gmail.com",
  FACEBOOK: "https://www.facebook.com/share/1APwzgTFqE/",

  // ── Hero Image ─────────────────────────────────────────────
  // Place your hero image in public/images/hero.png (or .png/.webp)
  HERO_IMAGE: "/images/hero.png",
  VENUE_NAME: "South Croydon United Church",
  VENUE_ADDRESS: "7 Aberdeen Road, Croydon",
  VENUE_POSTCODE: "CR0 1EQ",
  VENUE_MAP_EMBED: "https://maps.google.com/maps?q=South+Croydon+United+Church+7+Aberdeen+Road+Croydon+CR0+1EQ&t=&z=16&ie=UTF8&iwloc=&output=embed",
};


/* ═══════════════════════════════════════════════════════════════
   2. GALLERY
   
   The gallery is driven by a CSV file and self-hosted folders.
   
   FOLDER STRUCTURE (inside your public/ directory):
   
     public/
       gallery/
         gallery.csv              ← the master list
         first-rehearsal/          ← one folder per album
           photo1.jpg
           photo2.jpg
           video1.mp4
         community-day/
           group-photo.jpg
           cake.jpg
   
   CSV FORMAT (gallery/gallery.csv):
   
     album,folder,date,filename,name,description
     First Rehearsal,first-rehearsal,2026-02-25,photo1.jpg,Warming up,Getting ready for our first session
     First Rehearsal,first-rehearsal,2026-02-25,video1.mp4,Group sing-along,Our first song together!
     Community Day,community-day,2026-03-15,group-photo.jpg,The whole gang,Everyone together
   
   ADDING A NEW ALBUM:
     1. Create a folder in public/gallery/ (e.g. "spring-social")
     2. Drop your photos/videos in it
     3. Add rows to gallery.csv
   
   SUPPORTED FORMATS:
     Images: jpg, jpeg, png, gif, webp
     Video:  mp4, webm, mov
   
   ═══════════════════════════════════════════════════════════════ */

const GALLERY_BASE_PATH = "/gallery";
const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "avi", "m4v"];

function isVideo(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext);
}

function useGallery() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${GALLERY_BASE_PATH}/gallery.csv`)
      .then(r => {
        if (!r.ok) throw new Error("No gallery CSV");
        return r.text();
      })
      .then(csv => {
        const lines = csv.split("\n").slice(1).filter(l => l.trim());
        const albumMap = {};
        const albumOrder = [];

        lines.forEach(line => {
          // Parse CSV (handles quoted fields with commas)
          const fields = [];
          let current = "";
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQuotes = !inQuotes; }
            else if (ch === "," && !inQuotes) { fields.push(current.trim()); current = ""; }
            else { current += ch; }
          }
          fields.push(current.trim());

          const [albumName, folder, date, filename, name, description] = fields;
          if (!albumName || !folder || !filename) return;

          if (!albumMap[folder]) {
            albumMap[folder] = { title: albumName, folder, date: date || "", items: [] };
            albumOrder.push(folder);
          }

          albumMap[folder].items.push({
            src: `${GALLERY_BASE_PATH}/${folder}/${filename}`,
            filename,
            name: name || filename,
            description: description || "",
            isVideo: isVideo(filename),
          });
        });

        setAlbums(albumOrder.map(f => albumMap[f]));
        setLoading(false);
      })
      .catch(() => {
        // Fallback: no CSV found, show empty state
        setAlbums([]);
        setLoading(false);
      });
  }, []);

  return { albums, loading };
}


/* ═══════════════════════════════════════════════════════════════
   3. DOCUMENTS (Members Area)
   
   TO ADD A NEW DOCUMENT: Copy the template at the bottom.
   For Google Drive: Upload → Right-click → Share → Copy link
   ═══════════════════════════════════════════════════════════════ */

const MEMBER_DOCUMENTS = [
  { name: "Constitution", desc: "Our founding governance document", url: "#", icon: "📜" },
  { name: "Policies", desc: "Code of Conduct, Safeguarding & more", url: "#", icon: "📋" },
  { name: "Song Lyrics Pack", desc: "Current repertoire lyrics", url: "#", icon: "🎵" },
  { name: "Rehearsal Schedule", desc: "Term dates and times", url: "#", icon: "📅" },

  /*
  ── TEMPLATE ────────────────────────────────────────────────
  { name: "Doc Name", desc: "Short description", url: "https://drive.google.com/...", icon: "📄" },
  ─────────────────────────────────────────────────────────────
  */
];


/* ═══════════════════════════════════════════════════════════════
   4. UPCOMING DATES (Homepage)
   ═══════════════════════════════════════════════════════════════ */

const UPCOMING_DATES = [
  { day: "25", month: "Feb", title: "First Rehearsal!", time: "Doors 5pm · Rehearsal 7–9pm", desc: "Our very first session at South Croydon United Church!" },
  { day: "05", month: "Mar", title: "Rehearsal", time: "Doors 5pm · Rehearsal 7–9pm", desc: "Week 2 — building on our first songs." },
  { day: "12", month: "Mar", title: "Rehearsal", time: "Doors 5pm · Rehearsal 7–9pm", desc: "Week 3 — harmonies begin!" },

  /*
  ── TEMPLATE ────────────────────────────────────────────────
  { day: "01", month: "Apr", title: "Event Name", time: "7:00 pm", desc: "Description." },
  ─────────────────────────────────────────────────────────────
  */
];


/* ═══════════════════════════════════════════════════════════════
   5. FAQ
   ═══════════════════════════════════════════════════════════════ */

const FAQ_ITEMS = [
  { q: "Do I need to audition?", a: "No audition. Later on, we might check your vocal range privately so we can place you comfortably — just you and the choir leader, no stress." },
  { q: "I can't read music — is that okay?", a: "Completely fine! We teach everything by ear and provide lyric sheets. You don't need any musical background at all." },
  { q: "What kind of music will we sing?", a: "A mix of styles focusing on pop. We'll begin with familiar hits to get started with confidence and fun, branching out as we grow." },
  { q: "Is it just for LGBTQIA+ people?", a: "The choir centres LGBTQIA+ people but allies are warmly welcome too. It's about building an inclusive community where everyone belongs." },
  { q: "How much will it cost?", a: "We'll have a suggested monthly donation (£8, whatever you can afford) rather than a fixed fee. Times are tough — we know it — and no one is ever turned away." },
  { q: "I'm nervous about singing — is that okay?", a: "Absolutely. You're welcome to listen at first and join in when it feels right. This is a low-pressure, encouraging space." },
  { q: "Do I need to come every week?", a: "Come when you can. Life happens. There's no attendance register and no pressure." },
  { q: "Is it just singing?", a: "Singing is at the heart of it, but we're building a community. Expect socials, outings, open mic nights, and more as we grow." },
  { q: "I WON'T sing! Can I still be involved?", a: "Absobloodylutely! It is a community after all — you can come for the social aspect, and there are lots of ways you can help." },
  { q: "I have accessibility needs — can you accommodate me?", a: "We'll do our best! Let us know what you need and we'll work it out together. Step-free access info and lyric supports are available." },
];


/* ═══════════════════════════════════════════════════════════════
   6. WHAT TO EXPECT AT REHEARSAL
   
   Edit these to update the "What to Expect" page content.
   ═══════════════════════════════════════════════════════════════ */

const REHEARSAL_TIMELINE = [
  { time: "5:00 pm", title: "Doors open", desc: "Come early if you're heading straight from work. Grab a cuppa, settle in, and say hello." },
  { time: "7:00 pm", title: "Warm-up", desc: "We'll start with some gentle vocal warm-ups and breathing exercises. Nothing scary — everyone does it together." },
  { time: "7:20 pm", title: "Learning songs", desc: "We'll work through songs section by section. Everything is taught by ear — no sheet music needed. Lyric sheets provided." },
  { time: "8:15 pm", title: "Putting it together", desc: "We'll bring the parts together and sing as a full group. This is where the magic happens." },
  { time: "8:45 pm", title: "Wind down", desc: "A final run-through of what we've learned, any announcements, and a chance to chat." },
  { time: "9:00 pm", title: "Finish", desc: "That's a wrap! Some people head off, some stick around for a natter. No rush either way." },
];

const WHAT_TO_BRING = [
  "Yourself — that's genuinely all you need",
  "Water bottle (there'll be water available too)",
  "Comfortable clothes — nothing fancy",
  "A pen if you like to scribble on lyric sheets",
  "A friend, if you'd like — everyone's welcome",
];

const FIRST_TIME_TIPS = [
  { title: "You don't need to know anyone", desc: "Most people come on their own the first time. We'll make sure you feel welcome from the moment you walk in." },
  { title: "You don't need to sing right away", desc: "Feel free to listen, hum along, or just soak it in. Join in when you're ready — there's zero pressure." },
  { title: "There's no wrong voice", desc: "We're not looking for perfection. Every voice adds something. If you can talk, you can sing." },
  { title: "Come when you can", desc: "Missed a week? No worries. You won't be behind — we'll catch you up." },
  { title: "It's okay to feel nervous", desc: "Most people are! That feeling usually disappears about five minutes in." },
];


/* ═══════════════════════════════════════════════════════════════
   7. STYLES
   ═══════════════════════════════════════════════════════════════ */

const rainbowGrad = "linear-gradient(135deg, #e40303, #ff8c00, #ffed00, #008026, #004dff, #750787)";
const subtleRainbow = "linear-gradient(135deg, rgba(228,3,3,0.15), rgba(255,140,0,0.15), rgba(255,237,0,0.12), rgba(0,128,38,0.15), rgba(0,77,255,0.15), rgba(117,7,135,0.15))";

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;0,9..144,800;1,9..144,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0c0a13; --bg-raised: #16131f; --bg-card: #1c1828; --bg-card-hover: #231e30;
    --text: #f0eef5; --text-dim: #a09bb0; --text-muted: #716b82;
    --accent: #b068f9; --accent-glow: rgba(176,104,249,0.2);
    --font-display: 'Fraunces', Georgia, serif; --font-body: 'DM Sans', system-ui, sans-serif;
    --radius: 12px; --radius-lg: 18px; --transition: 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  html { scroll-behavior: smooth; }
  body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.65; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
  ::selection { background: var(--accent); color: #fff; }
  a { color: inherit; text-decoration: none; }
  .rainbow-line { height: 3px; background: ${rainbowGrad}; border-radius: 2px; opacity: 0.5; }
  .container { max-width: 72rem; margin: 0 auto; padding: 0 1.5rem; }
  @keyframes float-up { 0% { transform: translateY(0) scale(1); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(-100vh) scale(0.5); opacity: 0; } }
  @keyframes drift { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(30px); } }
  .particle { position: absolute; border-radius: 50%; pointer-events: none; animation: float-up linear infinite; }
  .particle-drift { animation: drift ease-in-out infinite; }
  .lightbox-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease; backdrop-filter: blur(8px); }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
  ::-webkit-scrollbar { width: 8px; } ::-webkit-scrollbar-track { background: var(--bg); } ::-webkit-scrollbar-thumb { background: #332e42; border-radius: 4px; }
  .white-section { background: #faf9fc; color: #1a1726; }
  .form-iframe { width: 100%; border: 0; border-radius: var(--radius); background: #fff; }
  input:focus, textarea:focus { outline: none; border-color: var(--accent) !important; }
  @media (max-width: 768px) { .nav-links-desktop { display: none !important; } .mobile-menu-btn { display: flex !important; } }
  @media (max-width: 900px) { .hero-grid { grid-template-columns: 1fr !important; } .hero-image-wrap { width: 160px !important; height: 160px !important; margin: 0 auto; } .hero-image-wrap > div:first-child { width: 160px !important; height: 160px !important; } }
  @media (min-width: 769px) { .mobile-menu-btn { display: none !important; } .mobile-menu-overlay { display: none !important; } }
`;


/* ═══════════════════════════════════════════════════════════════
   8. COMPONENTS
   ═══════════════════════════════════════════════════════════════ */

const RAINBOW_COLOURS = ["#e40303", "#ff8c00", "#ffed00", "#008026", "#004dff", "#750787"];

function AnimatedBackground() {
  const particles = Array.from({ length: 24 }, (_, i) => {
    const c = RAINBOW_COLOURS[i % RAINBOW_COLOURS.length];
    const s = 4 + Math.random() * 8;
    const l = Math.random() * 100;
    const d = 12 + Math.random() * 20;
    const dl = Math.random() * -20;
    const dd = 4 + Math.random() * 6;
    const o = 0.15 + Math.random() * 0.25;
    return (<div key={i} className="particle" style={{ width: s, height: s, background: c, opacity: o, left: `${l}%`, bottom: `-${s}px`, animation: `float-up ${d}s linear ${dl}s infinite` }}>
      <div className="particle-drift" style={{ width: "100%", height: "100%", borderRadius: "50%", animation: `drift ${dd}s ease-in-out ${dl}s infinite` }} />
    </div>);
  });
  return (<div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
    {particles}
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 25% 30%, rgba(176,104,249,0.07) 0%, transparent 60%), radial-gradient(ellipse at 75% 70%, rgba(228,3,3,0.04) 0%, transparent 50%), radial-gradient(ellipse at 50% 90%, rgba(0,77,255,0.04) 0%, transparent 40%)" }} />
  </div>);
}

// ── Facebook icon ──
function FacebookIcon({ size = 18 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>);
}

// ── Nav ──
function Nav({ page, setPage }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = [
    { label: "Home", key: "home" },
    { label: "What to Expect", key: "expect" },
    { label: "Dates & News", key: "dates" },
    { label: "Gallery", key: "gallery" },
    { label: "Members", key: "members" },
  ];
  const go = (k) => { setPage(k); setMobileOpen(false); };

  return (<>
    <header style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(12,10,19,0.85)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
        <button onClick={() => go("home")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: "var(--text)" }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: rainbowGrad, flexShrink: 0 }} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.05rem" }}>Croydon Inclusive Choir</span>
        </button>
        <nav className="nav-links-desktop" style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          {links.map((l) => (
            <button key={l.key} onClick={() => go(l.key)} style={{
              background: "none", border: "none", cursor: "pointer",
              color: page === l.key ? "var(--text)" : "var(--text-dim)",
              fontFamily: "var(--font-body)", fontSize: "0.86rem",
              fontWeight: page === l.key ? 600 : 400,
              borderBottom: page === l.key ? "2px solid var(--accent)" : "2px solid transparent",
              paddingBottom: 2,
            }}>{l.label}</button>
          ))}
          <button onClick={() => go("contact")} style={{
            background: subtleRainbow, border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 999, padding: "6px 16px", cursor: "pointer",
            color: "var(--text)", fontFamily: "var(--font-body)", fontSize: "0.84rem", fontWeight: 600,
          }}>Newsletter / Contact</button>
        </nav>
        <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} style={{ display: "none", background: "none", border: "none", cursor: "pointer", color: "var(--text)", padding: 8 }} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 12h18M3 6h18M3 18h18" />}
          </svg>
        </button>
      </div>
    </header>
    {mobileOpen && (
      <div className="mobile-menu-overlay" style={{ position: "fixed", inset: 0, zIndex: 99, background: "rgba(12,10,19,0.97)", backdropFilter: "blur(20px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2rem", paddingTop: 64 }}>
        {links.map((l) => (<button key={l.key} onClick={() => go(l.key)} style={{ background: "none", border: "none", cursor: "pointer", color: page === l.key ? "var(--text)" : "var(--text-dim)", fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 600 }}>{l.label}</button>))}
        <button onClick={() => go("contact")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 600 }}>Newsletter / Contact</button>
      </div>
    )}
  </>);
}

// ── Footer ──
function Footer({ setPage }) {
  return (
    <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "3rem 0 2rem" }}>
      <div className="container">
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "2rem" }}>
          <div>
            <p style={{ fontSize: "0.88rem", color: "var(--text-dim)" }}>© {new Date().getFullYear()} Croydon Inclusive Choir</p>
            <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 4 }}>An LGBTQIA+ community choir in Croydon</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", alignItems: "flex-end" }}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <a href={`mailto:${CONFIG.EMAIL}`} style={{ fontSize: "0.85rem", color: "var(--accent)" }}>{CONFIG.EMAIL}</a>
              <a href={CONFIG.FACEBOOK} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-dim)", display: "flex", alignItems: "center" }} aria-label="Facebook"><FacebookIcon size={20} /></a>
            </div>
            <div style={{ display: "flex", gap: "1.25rem", fontSize: "0.82rem" }}>
              <button onClick={() => setPage("contact")} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "0.82rem" }}>Newsletter</button>
              <button onClick={() => setPage("contact")} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "0.82rem" }}>Contact</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Lightbox (supports photos and video) ──
function Lightbox({ items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") onPrev(); if (e.key === "ArrowRight") onNext(); };
    window.addEventListener("keydown", h); document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);
  const item = items[index];
  const bs = { background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: 48, height: 48, cursor: "pointer", color: "#fff", fontSize: "1.3rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
  return (<div className="lightbox-overlay" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", maxWidth: "92vw" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", width: "100%" }}>
        <button onClick={onPrev} style={bs}>‹</button>
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          {item.isVideo ? (
            <video src={item.src} controls autoPlay style={{ maxWidth: "80vw", maxHeight: "78vh", borderRadius: 8, animation: "scaleIn 0.25s ease" }} />
          ) : (
            <img src={item.src} alt={item.name || ""} style={{ maxWidth: "80vw", maxHeight: "78vh", borderRadius: 8, objectFit: "contain", animation: "scaleIn 0.25s ease" }} />
          )}
        </div>
        <button onClick={onNext} style={bs}>›</button>
      </div>
      <div style={{ textAlign: "center" }}>
        {item.name && <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.95rem", fontWeight: 600 }}>{item.name}</p>}
        {item.description && <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", fontStyle: "italic", marginTop: "0.2rem" }}>{item.description}</p>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>{index + 1} / {items.length}</span>
        <button onClick={onClose} style={{ ...bs, width: "auto", borderRadius: 999, padding: "8px 20px", fontSize: "0.85rem" }}>Close ✕</button>
      </div>
    </div>
  </div>);
}

// ── Announcements hook ──
function useAnnouncements() {
  const [a, setA] = useState([]);
  useEffect(() => {
    if (!CONFIG.ANNOUNCEMENTS_CSV_URL) {
      setA([
        { date: "2026-02-20", title: "Welcome!", content: "We rehearse every Wednesday at South Croydon United Church. Join the newsletter to stay in the loop!", pinned: true },
        { date: "2026-02-18", title: "Website live", content: "Our new website is up and running. Have a look around and get in touch if you have questions.", pinned: false },
      ]);
      return;
    }
    fetch(CONFIG.ANNOUNCEMENTS_CSV_URL).then(r => r.text()).then(csv => {
      const lines = csv.split("\n").slice(1).filter(l => l.trim());
      setA(lines.map(line => { const [date, title, content, pinned] = line.split(",").map(s => s.replace(/^"|"$/g, "").trim()); return { date, title, content, pinned: pinned?.toLowerCase() === "yes" }; }).sort((a, b) => new Date(b.date) - new Date(a.date)));
    }).catch(() => {});
  }, []);
  return a;
}

// ── Formspree form hook ──
function useFormspree(endpoint) {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const submit = async (data) => {
    setStatus("sending");
    try {
      const res = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(data) });
      setStatus(res.ok ? "sent" : "error");
    } catch { setStatus("error"); }
  };
  return { status, submit, reset: () => setStatus("idle") };
}

// ── Google Calendar Events Hook ────────────────────────────────
function useCalendarEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!CONFIG.GOOGLE_CALENDAR_API_KEY) {
      // Fallback to manual UPCOMING_DATES when no API key is set
      const fallback = UPCOMING_DATES.map((d, i) => {
        const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
        const monthNum = months[d.month] ?? 0;
        const year = new Date().getFullYear();
        const start = new Date(year, monthNum, parseInt(d.day));
        return {
          id: `manual-${i}`, title: d.title, description: d.desc,
          location: "", start, end: null, isAllDay: false, isManual: true,
          displayDate: `${d.day} ${d.month}`,
          displayTime: d.time,
        };
      });
      setEvents(fallback);
      setLoading(false);
      return;
    }

    const now = new Date().toISOString();
    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CONFIG.GOOGLE_CALENDAR_ID)}/events?key=${CONFIG.GOOGLE_CALENDAR_API_KEY}&timeMin=${now}&orderBy=startTime&singleEvents=true&maxResults=50`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (data.items) {
          setEvents(data.items.map(ev => {
            const start = new Date(ev.start.dateTime || ev.start.date);
            const end = ev.end ? new Date(ev.end.dateTime || ev.end.date) : null;
            const isAllDay = !ev.start.dateTime;
            return {
              id: ev.id, title: ev.summary || "Event",
              description: ev.description || "", location: ev.location || "",
              start, end, isAllDay,
              displayDate: start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
              displayTime: isAllDay ? "All day" : `${start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} – ${end ? end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : ""}`,
            };
          }));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return { events, loading };
}

// ── Calendar Month View Component ──────────────────────────────
function CalendarMonth({ events }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const monthName = currentMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay + 6) % 7; // Mon=0
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const eventsOnDay = (d) => {
    if (!d) return [];
    return events.filter(ev => {
      if (!ev.start) return false;
      return ev.start.getFullYear() === year && ev.start.getMonth() === month && ev.start.getDate() === d;
    });
  };

  const selectedEvents = selectedDay ? eventsOnDay(selectedDay) : [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: 36, height: 36, cursor: "pointer", color: "var(--text-dim)", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
        <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.15rem" }}>{monthName}</h3>
        <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "50%", width: 36, height: 36, cursor: "pointer", color: "var(--text-dim)", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: "0.72rem", fontWeight: 600, color: "var(--text-muted)", padding: "6px 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          const dayEvents = eventsOnDay(d);
          const hasEvents = dayEvents.length > 0;
          const isSelected = d === selectedDay;
          return (
            <div key={i} onClick={() => d && setSelectedDay(isSelected ? null : d)} style={{
              aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              borderRadius: "var(--radius)", cursor: d ? "pointer" : "default",
              background: isSelected ? "var(--accent)" : isToday(d) ? "rgba(176,104,249,0.15)" : d ? "var(--bg-card)" : "transparent",
              border: isToday(d) && !isSelected ? "1px solid rgba(176,104,249,0.3)" : "1px solid transparent",
              transition: "all 0.15s ease",
            }}>
              {d && <>
                <span style={{ fontSize: "0.88rem", fontWeight: isToday(d) || hasEvents ? 700 : 400, color: isSelected ? "#fff" : "var(--text)" }}>{d}</span>
                {hasEvents && (
                  <div style={{ display: "flex", gap: 2, marginTop: 2 }}>
                    {dayEvents.slice(0, 3).map((_, j) => (
                      <span key={j} style={{ width: 5, height: 5, borderRadius: "50%", background: isSelected ? "#fff" : "var(--accent)" }} />
                    ))}
                  </div>
                )}
              </>}
            </div>
          );
        })}
      </div>

      {selectedDay && (
        <div style={{ marginTop: "1.25rem" }}>
          <p style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--accent)", marginBottom: "0.75rem" }}>
            {new Date(year, month, selectedDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {selectedEvents.length > 0 ? (
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {selectedEvents.map(ev => (
                <div key={ev.id} style={{ padding: "0.75rem 1rem", background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "var(--radius)" }}>
                  <h4 style={{ fontWeight: 600, fontSize: "0.92rem" }}>{ev.title}</h4>
                  <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>{ev.displayTime}</p>
                  {ev.description && <p style={{ fontSize: "0.85rem", color: "var(--text-dim)", marginTop: "0.3rem", whiteSpace: "pre-wrap" }}>{ev.description}</p>}
                  {ev.location && <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>📍 {ev.location}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: "var(--text-muted)", fontSize: "0.88rem" }}>No events on this day.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Small UI pieces ──
function SectionHead({ title, subtitle, light }) {
  return (<div style={{ marginBottom: "2rem" }}>
    <h2 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 700, letterSpacing: "-0.02em", color: light ? "#1a1726" : "var(--text)" }}>{title}</h2>
    {subtitle && <p style={{ color: light ? "#666" : "var(--text-dim)", marginTop: "0.4rem", fontSize: "1rem" }}>{subtitle}</p>}
  </div>);
}

function Card({ children, style, hover, onClick }) {
  const [h, setH] = useState(false);
  return (<div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
    background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "var(--radius-lg)", padding: "1.5rem", transition: "all var(--transition)",
    ...(hover && h ? { background: "var(--bg-card-hover)", borderColor: "rgba(255,255,255,0.12)", transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } : {}),
    ...(onClick ? { cursor: "pointer" } : {}), ...style,
  }}>{children}</div>);
}

function Pill({ children }) {
  return (<span style={{ background: subtleRainbow, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, padding: "5px 14px", fontSize: "0.8rem", fontWeight: 500, color: "var(--text-dim)", whiteSpace: "nowrap" }}>{children}</span>);
}

// ── Page header ──
function PageHeader({ title, subtitle }) {
  return (<section style={{ padding: "4rem 0 2rem", background: "radial-gradient(ellipse at 50% 0%, rgba(176,104,249,0.06) 0%, transparent 60%)" }}>
    <div className="container">
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 700 }}>{title}</h1>
      {subtitle && <p style={{ color: "var(--text-dim)", marginTop: "0.4rem" }}>{subtitle}</p>}
    </div>
  </section>);
}


/* ═══════════════════════════════════════════════════════════════
   9. PAGES
   ═══════════════════════════════════════════════════════════════ */


/* ── HOME PAGE ──────────────────────────────────────────────── */

function HomePage({ setPage }) {
  const announcements = useAnnouncements();
  const { events } = useCalendarEvents();
  const [openFaq, setOpenFaq] = useState(null);

  return (<div>

    {/* ── Hero ── */}
    <section style={{ position: "relative", padding: "6rem 0 5rem", overflow: "hidden" }}>
      <AnimatedBackground />
      <div className="container" style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "inline-block", background: subtleRainbow, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, padding: "5px 16px", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-dim)", marginBottom: "1.5rem" }}>
          🎉 Every Wednesday evening — come along any time
        </div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem, 4.5vw, 3.2rem)", fontWeight: 800, lineHeight: 1.08, letterSpacing: "-0.03em" }}>
          Come as you are.{" "}
          <span style={{ backgroundImage: rainbowGrad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Sing with us.</span>
        </h1>

        {/* Text + image side by side */}
        <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "2.5rem", alignItems: "center", marginTop: "1.5rem" }}>
          <div>
            <p style={{ fontSize: "1.05rem", color: "var(--text-dim)", lineHeight: 1.75 }}>
              A community choir in Croydon built around belonging. No auditions, no expectations — just people coming together to sing, connect, and look out for each other. Come when you can, stay as long as you like.
            </p>
            <p style={{ marginTop: "1rem", fontSize: "0.95rem", color: "var(--text-muted)", lineHeight: 1.75 }}>
              And it's not just about singing. We're building something bigger in terms of the community like outings, open mic nights, and whatever else we fancy doing in the future.
            </p>
          </div>
          <div className="hero-image-wrap" style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 60px rgba(0,0,0,0.4)", width: 220, height: 220 }}>
              <img src={CONFIG.HERO_IMAGE} alt="Croydon Inclusive Choir" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div style={{ position: "absolute", top: -5, right: -5, bottom: -5, width: 5, borderRadius: "0 var(--radius-lg) var(--radius-lg) 0", background: rainbowGrad, opacity: 0.5 }} />
          </div>
        </div>

        {/* Buttons + pills below */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginTop: "2rem" }}>
          <button onClick={() => setPage("contact")} style={{ background: "var(--accent)", color: "#fff", border: "none", borderRadius: 999, padding: "13px 30px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Join newsletter</button>
          <button onClick={() => setPage("expect")} style={{ background: "transparent", color: "var(--text)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "13px 30px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>What to expect</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1.75rem" }}>
          {["No audition", "Come when you can", "Pop & familiar songs", "More than just singing", "All voices welcome"].map(t => <Pill key={t}>{t}</Pill>)}
        </div>
      </div>
    </section>

    {/* ── Rehearsal details card ── */}
    <section style={{ padding: "0 0 3rem" }}>
      <div className="container" style={{ maxWidth: "54rem" }}>
        <Card style={{ background: "linear-gradient(135deg, var(--bg-card) 0%, rgba(176,104,249,0.05) 100%)", border: "1px solid rgba(176,104,249,0.15)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.5rem" }}>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.4rem" }}>When</p>
              <p style={{ fontWeight: 700, fontSize: "1rem" }}>Every Wednesday</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem", marginTop: "0.2rem" }}>Doors open from 5:00 pm (for those coming straight from work)</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem" }}>Rehearsal: 7:00 pm – 9:00 pm</p>
            </div>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.4rem" }}>Where</p>
              <p style={{ fontWeight: 700, fontSize: "1rem" }}>{CONFIG.VENUE_NAME}</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem", marginTop: "0.2rem" }}>{CONFIG.VENUE_ADDRESS}</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem" }}>{CONFIG.VENUE_POSTCODE}</p>
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(CONFIG.VENUE_NAME + " " + CONFIG.VENUE_ADDRESS + " " + CONFIG.VENUE_POSTCODE)}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: "0.85rem", marginTop: "0.3rem", display: "inline-block" }}>View on map ↗</a>
            </div>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.4rem" }}>How much</p>
              <p style={{ fontWeight: 700, fontSize: "1rem" }}>Suggested donation</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem", marginTop: "0.2rem" }}>£8, whatever you can afford. No one is turned away.</p>
            </div>
          </div>
          <div style={{ marginTop: "1.25rem", paddingTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center" }}>
            <span style={{ color: "var(--text-dim)", fontSize: "0.88rem" }}>Just come along to a rehearsal — that's how you become a member.</span>
            <button onClick={() => setPage("expect")} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "6px 16px", color: "var(--text-dim)", fontSize: "0.82rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>What to expect →</button>
          </div>
        </Card>
      </div>
    </section>

    {/* ── Announcements preview ── */}
    <section style={{ padding: "3rem 0 4rem" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <SectionHead title="Latest news" subtitle="What's happening and what's coming up." />
          <button onClick={() => setPage("dates")} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "8px 20px", color: "var(--text-dim)", fontSize: "0.85rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>See all news →</button>
        </div>
        <div style={{ display: "grid", gap: "1rem" }}>
          {announcements.slice(0, 3).map((a, i) => (
            <Card key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                <div>
                  {a.pinned && <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>📌 Pinned</span>}
                  <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.1rem", marginTop: a.pinned ? "0.25rem" : 0 }}>{a.title}</h3>
                  <p style={{ color: "var(--text-dim)", marginTop: "0.35rem", fontSize: "0.92rem" }}>{a.content}</p>
                </div>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{a.date}</span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <div className="container"><div className="rainbow-line" /></div>

    {/* ── Upcoming Dates ── */}
    <section style={{ padding: "4rem 0" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <SectionHead title="Upcoming dates" subtitle="What's in the diary." />
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={() => setPage("dates")} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "8px 20px", color: "var(--text-dim)", fontSize: "0.85rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Full calendar →</button>
            <a href={CONFIG.CALENDAR_ICAL_URL} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "8px 16px", color: "var(--text-dim)", fontSize: "0.85rem", fontFamily: "var(--font-body)", textDecoration: "none" }}>📅 Subscribe</a>
          </div>
        </div>
        <div style={{ display: "grid", gap: "0.75rem", maxWidth: "36rem" }}>
          {events.slice(0, 4).map((ev) => (
            <Card key={ev.id} hover>
              <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
                <div style={{ width: 56, height: 60, borderRadius: "var(--radius)", background: subtleRainbow, border: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {ev.start ? <>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1 }}>{ev.start.getDate()}</span>
                    <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase" }}>{ev.start.toLocaleDateString("en-GB", { month: "short" })}</span>
                  </> : <>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1 }}>{ev.displayDate.split(" ")[0]}</span>
                    <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase" }}>{ev.displayDate.split(" ")[1]}</span>
                  </>}
                </div>
                <div>
                  <h4 style={{ fontWeight: 600, fontSize: "1rem" }}>{ev.title}</h4>
                  <p style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{ev.displayTime}</p>
                  {ev.description && <p style={{ fontSize: "0.85rem", color: "var(--text-dim)", marginTop: "0.2rem" }}>{ev.description}</p>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* ── About — white section ── */}
    <div className="white-section" style={{ padding: "4.5rem 0" }}>
      <div className="container" style={{ maxWidth: "52rem" }}>
        <SectionHead title="It's more than just the music (which why it doesn't matter too much if you can't sing!)" light />
        <div style={{ color: "#444", lineHeight: 1.85, fontSize: "1rem" }}>
          <p>Croydon Inclusive Choir is an LGBTQIA+ community choir for Croydon and the surrounding areas. We're here to bring people together — through singing, through community, and through looking out for one another.</p>
          <p style={{ marginTop: "1rem" }}>Singing is at the heart of what we do, but it doesn't stop there. We'll be organising socials, community events, outings, open mic nights, and more as we grow. This is a space to make friends, build confidence, and feel part of something.</p>
          <p style={{ marginTop: "1rem" }}>There's no audition and no attendance pressure. Come when you can. Just show up and be yourself.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginTop: "2rem" }}>
          {[
            { icon: "", title: "Inclusive & welcoming", desc: "LGBTQIA+ people and allies" },
            { icon: "", title: "Community-first", desc: "Friends, music, and belonging" },
            { icon: "", title: "More than singing", desc: "Socials, events, and outings" },
            { icon: "", title: "Accessible", desc: "Step-free info & lyric supports" },
          ].map(v => (
            <div key={v.title} style={{ background: "#fff", border: "1px solid #e8e5ef", borderRadius: "var(--radius-lg)", padding: "1.5rem" }}>
              <span style={{ fontSize: "1.5rem" }}>{v.icon}</span>
              <h4 style={{ fontWeight: 700, marginTop: "0.5rem", fontSize: "0.95rem" }}>{v.title}</h4>
              <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.25rem" }}>{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ── Where section ── */}
    <section style={{ padding: "4rem 0" }}>
      <div className="container" style={{ maxWidth: "52rem" }}>
        <SectionHead title="Where to find us" subtitle="We rehearse at South Croydon United Church." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem" }}>
          <Card>
            <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.5rem" }}>Venue</p>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.15rem" }}>{CONFIG.VENUE_NAME}</h3>
            <p style={{ color: "var(--text-dim)", marginTop: "0.3rem", fontSize: "0.92rem" }}>{CONFIG.VENUE_ADDRESS}</p>
            <p style={{ color: "var(--text-dim)", fontSize: "0.92rem" }}>{CONFIG.VENUE_POSTCODE}</p>
            <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(CONFIG.VENUE_NAME + " " + CONFIG.VENUE_ADDRESS + " " + CONFIG.VENUE_POSTCODE)}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: "0.88rem" }}>📍 Google Maps ↗</a>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>Nearest stations: South Croydon, East Croydon</p>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>Bus routes nearby · Street parking available</p>
            </div>
          </Card>
          <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)", minHeight: 220 }}>
            <iframe src={CONFIG.VENUE_MAP_EMBED} style={{ width: "100%", height: "100%", minHeight: 220, border: 0 }} title="Venue map" loading="lazy" allowFullScreen />
          </div>
        </div>
      </div>
    </section>

    {/* ── FAQ ── */}
    <div className="white-section" style={{ padding: "4rem 0" }}>
      <div className="container" style={{ maxWidth: "48rem" }}>
        <SectionHead title="FAQ" subtitle="Common questions answered." light />
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e8e5ef", borderRadius: "var(--radius)", overflow: "hidden", ...(openFaq === i ? { borderColor: "#b068f9" } : {}) }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "1.1rem 1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#1a1726", fontFamily: "var(--font-body)", fontSize: "0.95rem", fontWeight: 600, textAlign: "left" }}>
                {item.q}
                <span style={{ transform: openFaq === i ? "rotate(45deg)" : "none", transition: "transform 0.2s", fontSize: "1.2rem", color: "#b068f9" }}>+</span>
              </button>
              {openFaq === i && <div style={{ padding: "0 1.5rem 1.25rem", color: "#555", fontSize: "0.92rem", lineHeight: 1.7 }}>{item.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ── Facebook + Newsletter CTA ── */}
    <section style={{ padding: "4rem 0" }}>
      <div className="container" style={{ maxWidth: "40rem", textAlign: "center" }}>
        <SectionHead title="Stay connected" subtitle="Join the newsletter or follow us on Facebook." />
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "1rem" }}>
          <button onClick={() => setPage("contact")} style={{ background: "var(--accent)", color: "#fff", border: "none", borderRadius: 999, padding: "13px 28px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Join newsletter</button>
          <a href={CONFIG.FACEBOOK} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "transparent", color: "var(--text)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "13px 28px", fontWeight: 600, fontSize: "0.95rem", fontFamily: "var(--font-body)" }}>
            <FacebookIcon size={16} /> Facebook
          </a>
          <button onClick={() => setPage("contact")} style={{ background: "transparent", color: "var(--text)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "13px 28px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Contact us</button>
        </div>
      </div>
    </section>
  </div>);
}


/* ── WHAT TO EXPECT PAGE ────────────────────────────────────── */

function ExpectPage({ setPage }) {
  return (<div>
    <PageHeader title="What to Expect" subtitle="Everything you need to know before your first rehearsal (and every one after that)." />

    {/* ── Overview ── */}
    <section style={{ padding: "2rem 0 4rem" }}>
      <div className="container" style={{ maxWidth: "48rem" }}>
        <div style={{ color: "var(--text-dim)", lineHeight: 1.85, fontSize: "1rem" }}>
          <p>Walking into something new takes guts — we get it. Here's exactly what happens at a rehearsal so you know what you're in for. Spoiler: it's relaxed, it's fun, and everyone's in the same boat.</p>
        </div>

        {/* Rehearsal details card */}
        <Card style={{ marginTop: "2rem", background: "linear-gradient(135deg, var(--bg-card) 0%, rgba(176,104,249,0.05) 100%)", border: "1px solid rgba(176,104,249,0.15)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.25rem" }}>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.3rem" }}>When</p>
              <p style={{ fontWeight: 700 }}>Every Wednesday</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem" }}>Doors: 5:00 pm (for those coming straight from work) · Rehearsal: 7:00–9:00 pm</p>
            </div>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.3rem" }}>Where</p>
              <p style={{ fontWeight: 700 }}>{CONFIG.VENUE_NAME}</p>
              <p style={{ color: "var(--text-dim)", fontSize: "0.88rem" }}>{CONFIG.VENUE_ADDRESS}, {CONFIG.VENUE_POSTCODE}</p>
            </div>
          </div>
        </Card>

        {/* Timeline */}
        <div style={{ marginTop: "3rem" }}>
          <SectionHead title="A typical rehearsal" subtitle="Here's how the evening flows." />
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {REHEARSAL_TIMELINE.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "1.25rem", alignItems: "flex-start" }}>
                <div style={{ width: 72, flexShrink: 0, textAlign: "right", paddingTop: 2 }}>
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.92rem", color: "var(--accent)" }}>{item.time}</span>
                </div>
                <div style={{ flex: 1, paddingBottom: "1.25rem", borderLeft: "2px solid rgba(176,104,249,0.2)", paddingLeft: "1.25rem" }}>
                  <h4 style={{ fontWeight: 700, fontSize: "1rem" }}>{item.title}</h4>
                  <p style={{ color: "var(--text-dim)", fontSize: "0.9rem", marginTop: "0.2rem", lineHeight: 1.65 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rainbow-line" style={{ margin: "3rem 0" }} />

        {/* First time tips */}
        <SectionHead title="First time?" subtitle="A few things that might help." />
        <div style={{ display: "grid", gap: "1rem" }}>
          {FIRST_TIME_TIPS.map((tip, i) => (
            <Card key={i}>
              <h4 style={{ fontWeight: 700, fontSize: "0.95rem" }}>{tip.title}</h4>
              <p style={{ color: "var(--text-dim)", fontSize: "0.9rem", marginTop: "0.3rem", lineHeight: 1.65 }}>{tip.desc}</p>
            </Card>
          ))}
        </div>

        <div className="rainbow-line" style={{ margin: "3rem 0" }} />

        {/* What to bring */}
        <SectionHead title="What to bring" />
        <Card>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {WHAT_TO_BRING.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: rainbowGrad, marginTop: 7 }} />
                <p style={{ color: "var(--text-dim)", fontSize: "0.92rem" }}>{item}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* CTA */}
        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <p style={{ color: "var(--text-dim)", fontSize: "1rem", marginBottom: "1rem" }}>Ready? Just come along to a rehearsal.</p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.75rem" }}>
            <button onClick={() => setPage("contact")} style={{ background: "var(--accent)", color: "#fff", border: "none", borderRadius: 999, padding: "12px 28px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Get in touch</button>
            <button onClick={() => setPage("dates")} style={{ background: "transparent", color: "var(--text)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "12px 28px", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>See dates</button>
          </div>
        </div>
      </div>
    </section>
  </div>);
}


/* ── DATES & NEWS PAGE ──────────────────────────────────────── */

function DatesPage() {
  const announcements = useAnnouncements();
  const { events, loading } = useCalendarEvents();
  const [view, setView] = useState("list"); // "list" | "calendar"

  return (<div>
    <PageHeader title="Dates & News" subtitle="Announcements, upcoming events, and the calendar." />

    {/* ── Upcoming Events ── */}
    <section style={{ padding: "2rem 0 4rem" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <SectionHead title="Upcoming events" subtitle={CONFIG.GOOGLE_CALENDAR_API_KEY ? "Pulled live from our Google Calendar." : "Update these in the code, or connect the Google Calendar API for live events."} />
          <div style={{ display: "flex", gap: "0.25rem", background: "var(--bg-card)", borderRadius: 999, padding: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
            <button onClick={() => setView("list")} style={{
              background: view === "list" ? "var(--accent)" : "transparent", color: view === "list" ? "#fff" : "var(--text-dim)",
              border: "none", borderRadius: 999, padding: "6px 16px", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
            }}>List</button>
            <button onClick={() => setView("calendar")} style={{
              background: view === "calendar" ? "var(--accent)" : "transparent", color: view === "calendar" ? "#fff" : "var(--text-dim)",
              border: "none", borderRadius: 999, padding: "6px 16px", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
            }}>Calendar</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem 0" }}>
            <p style={{ color: "var(--text-muted)" }}>Loading events...</p>
          </div>
        ) : view === "list" ? (
          /* ── List view ── */
          <div style={{ display: "grid", gap: "0.75rem", maxWidth: "42rem" }}>
            {events.length > 0 ? events.map((ev) => (
              <Card key={ev.id} hover>
                <div style={{ display: "flex", gap: "1.25rem", alignItems: "flex-start" }}>
                  <div style={{
                    width: 56, minHeight: 60, borderRadius: "var(--radius)", background: subtleRainbow,
                    border: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", flexShrink: 0, padding: "6px 4px",
                  }}>
                    {ev.start ? <>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1 }}>{ev.start.getDate()}</span>
                      <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase" }}>
                        {ev.start.toLocaleDateString("en-GB", { month: "short" })}
                      </span>
                    </> : <>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1 }}>{ev.displayDate.split(" ")[0]}</span>
                      <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase" }}>{ev.displayDate.split(" ")[1]}</span>
                    </>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontWeight: 600, fontSize: "1rem" }}>{ev.title}</h4>
                    <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.1rem" }}>
                      {ev.start ? ev.displayDate + " · " : ""}{ev.displayTime}
                    </p>
                    {ev.description && <p style={{ fontSize: "0.85rem", color: "var(--text-dim)", marginTop: "0.3rem", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{ev.description}</p>}
                    {ev.location && <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>📍 {ev.location}</p>}
                  </div>
                </div>
              </Card>
            )) : (
              <Card style={{ textAlign: "center", padding: "2.5rem" }}>
                <p style={{ color: "var(--text-muted)" }}>No upcoming events right now. Check back soon!</p>
              </Card>
            )}
          </div>
        ) : (
          /* ── Calendar view ── */
          <div style={{ maxWidth: "28rem" }}>
            <Card style={{ padding: "1.5rem" }}>
              <CalendarMonth events={events} />
            </Card>
          </div>
        )}
      </div>
    </section>

    <div className="container"><div className="rainbow-line" /></div>

    {/* ── Subscribe to calendar ── */}
    <section style={{ padding: "3rem 0" }}>
      <div className="container" style={{ maxWidth: "42rem" }}>
        <Card style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "1.25rem" }}>
          <div>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.05rem" }}>Add to your calendar</h3>
            <p style={{ color: "var(--text-dim)", fontSize: "0.88rem", marginTop: "0.25rem" }}>Subscribe and never miss a rehearsal or event.</p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            <a href={CONFIG.CALENDAR_GOOGLE_URL} target="_blank" rel="noopener noreferrer" style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              background: "var(--accent)", color: "#fff", border: "none", borderRadius: 999,
              padding: "9px 18px", fontSize: "0.85rem", fontWeight: 600, fontFamily: "var(--font-body)", textDecoration: "none",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/></svg>
              Google Calendar
            </a>
            <a href={CONFIG.CALENDAR_ICAL_URL} style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              background: "transparent", color: "var(--text)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999,
              padding: "9px 18px", fontSize: "0.85rem", fontWeight: 600, fontFamily: "var(--font-body)", textDecoration: "none",
            }}>
              📅 iCal / Outlook
            </a>
          </div>
        </Card>
      </div>
    </section>

    <div className="container"><div className="rainbow-line" /></div>

    {/* ── Announcements ── */}
    <section style={{ padding: "4rem 0" }}>
      <div className="container">
        <SectionHead title="Announcements" />
        <div style={{ display: "grid", gap: "1rem", maxWidth: "48rem" }}>
          {announcements.map((a, i) => (
            <Card key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                <div>
                  {a.pinned && <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>📌 Pinned</span>}
                  <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.05rem", marginTop: a.pinned ? "0.25rem" : 0 }}>{a.title}</h3>
                  <p style={{ color: "var(--text-dim)", marginTop: "0.35rem", fontSize: "0.9rem" }}>{a.content}</p>
                </div>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{a.date}</span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  </div>);
}


/* ── GALLERY PAGE ───────────────────────────────────────────── */

function GalleryPage() {
  /* ── GALLERY FUNCTIONALITY — COMMENTED OUT, UNCOMMENT WHEN READY ──
  const { albums, loading } = useGallery();
  const [lightbox, setLightbox] = useState(null);
  const allItems = albums.flatMap(a => a.items);
  const flatIndex = lightbox ? albums.slice(0, lightbox.albumIdx).reduce((sum, a) => sum + a.items.length, 0) + lightbox.itemIdx : 0;
  const openLightbox = (albumIdx, itemIdx) => setLightbox({ albumIdx, itemIdx });
  const closeLightbox = () => setLightbox(null);
  const prevItem = useCallback(() => {
    if (!lightbox) return;
    const newFlat = (flatIndex - 1 + allItems.length) % allItems.length;
    let count = 0;
    for (let ai = 0; ai < albums.length; ai++) {
      if (count + albums[ai].items.length > newFlat) {
        setLightbox({ albumIdx: ai, itemIdx: newFlat - count });
        return;
      }
      count += albums[ai].items.length;
    }
  }, [lightbox, flatIndex, allItems.length, albums]);
  const nextItem = useCallback(() => {
    if (!lightbox) return;
    const newFlat = (flatIndex + 1) % allItems.length;
    let count = 0;
    for (let ai = 0; ai < albums.length; ai++) {
      if (count + albums[ai].items.length > newFlat) {
        setLightbox({ albumIdx: ai, itemIdx: newFlat - count });
        return;
      }
      count += albums[ai].items.length;
    }
  }, [lightbox, flatIndex, allItems.length, albums]);
  ── END COMMENTED OUT GALLERY ── */

  return (<div>
    <PageHeader title="Gallery" subtitle="Photos and videos from our rehearsals, events, and community moments." />

    <section style={{ padding: "6rem 0" }}>
      <div className="container" style={{ textAlign: "center" }}>
        <p style={{ fontSize: "2.5rem", opacity: 0.4 }}>📸</p>
        <p style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.5rem", marginTop: "1rem", color: "var(--text-dim)" }}>Nothing here yet</p>
        <p style={{ color: "var(--text-muted)", marginTop: "0.5rem", fontSize: "0.92rem" }}>Check back after our first rehearsal!</p>
      </div>
    </section>

    {/* ── COMMENTED OUT: Full gallery rendering — uncomment when ready ──
    <section style={{ padding: "2rem 0 4rem" }}>
      <div className="container">
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem 0" }}>
            <p style={{ color: "var(--text-muted)" }}>Loading gallery...</p>
          </div>
        ) : albums.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 2rem", border: "2px dashed rgba(255,255,255,0.1)", borderRadius: "var(--radius-lg)" }}>
            <p style={{ fontSize: "2.5rem", opacity: 0.4 }}>📸</p>
            <p style={{ color: "var(--text-muted)", marginTop: "0.75rem" }}>Photos coming soon!</p>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", marginTop: "0.5rem" }}>Add a gallery.csv and photo folders to get started.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "4rem" }}>
            {albums.map((album, albumIdx) => (
              <div key={album.folder} id={`album-${album.folder}`}>
                <div style={{ marginBottom: "1.25rem", paddingBottom: "1rem", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                  <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.5rem" }}>{album.title}</h2>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "0.3rem" }}>
                    {album.date && <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{album.date}</span>}
                    <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                      {album.items.filter(i => !i.isVideo).length} photo{album.items.filter(i => !i.isVideo).length !== 1 ? "s" : ""}
                      {album.items.some(i => i.isVideo) && ` · ${album.items.filter(i => i.isVideo).length} video${album.items.filter(i => i.isVideo).length !== 1 ? "s" : ""}`}
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem" }}>
                  {album.items.map((item, itemIdx) => (
                    <div key={itemIdx} onClick={() => openLightbox(albumIdx, itemIdx)} style={{
                      cursor: "pointer", borderRadius: "var(--radius)", overflow: "hidden",
                      aspectRatio: "4/3", position: "relative", background: "var(--bg-card)",
                    }}
                      onMouseEnter={e => { const el = e.currentTarget.querySelector("img, video"); if (el) el.style.transform = "scale(1.05)"; e.currentTarget.querySelector(".overlay").style.opacity = 1; }}
                      onMouseLeave={e => { const el = e.currentTarget.querySelector("img, video"); if (el) el.style.transform = ""; e.currentTarget.querySelector(".overlay").style.opacity = 0; }}>
                      {item.isVideo ? (
                        <video src={item.src} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }} />
                      ) : (
                        <img src={item.src} alt={item.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }} />
                      )}
                      {item.isVideo && (
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 48, height: 48, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      )}
                      <div className="overlay" style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)", opacity: 0, transition: "opacity 0.2s", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "0.75rem" }}>
                        {item.name && <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{item.name}</span>}
                        {item.description && <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.78rem", marginTop: "0.15rem" }}>{item.description}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {albums.length > 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "3rem", paddingTop: "2rem", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ fontSize: "0.82rem", color: "var(--text-muted)", lineHeight: "32px" }}>Jump to:</span>
            {albums.map(a => (
              <a key={a.folder} href={`#album-${a.folder}`} style={{ display: "inline-block", padding: "6px 14px", fontSize: "0.82rem", color: "var(--text-dim)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, textDecoration: "none", fontFamily: "var(--font-body)" }}>{a.title}</a>
            ))}
          </div>
        )}
      </div>
    </section>
    <div className="container"><div className="rainbow-line" /></div>
    <section style={{ padding: "3rem 0" }}>
      <div className="container" style={{ maxWidth: "44rem" }}>
        <Card style={{ textAlign: "center" }}>
          <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.05rem" }}>A note on photos</h3>
          <p style={{ color: "var(--text-dim)", marginTop: "0.5rem", fontSize: "0.9rem", lineHeight: 1.7 }}>We respect everyone's privacy. We ask for photo consent as part of the membership form. If you'd like a photo removed, just let us know.</p>
          <a href={`mailto:${CONFIG.EMAIL}`} style={{ display: "inline-block", marginTop: "0.75rem", fontSize: "0.85rem", color: "var(--accent)" }}>{CONFIG.EMAIL}</a>
        </Card>
      </div>
    </section>
    {lightbox && <Lightbox items={allItems} index={flatIndex} onClose={closeLightbox} onPrev={prevItem} onNext={nextItem} />}
    ── END COMMENTED OUT GALLERY RENDERING ── */}

  </div>);
}


/* ── MEMBERS PAGE ───────────────────────────────────────────── */

function MembersPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("resources");
  const login = () => { if (pw === CONFIG.MEMBERS_PASSWORD) { setLoggedIn(true); setErr(false); } else setErr(true); };

  if (!loggedIn) return (<div>
    <PageHeader title="Members Area" subtitle="Resources, documents, and forms for choir members." />
    <section style={{ padding: "3rem 0 5rem" }}>
      <div className="container" style={{ maxWidth: "28rem" }}>
        <Card style={{ padding: "2.5rem" }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <span style={{ fontSize: "2.5rem" }}>🔒</span>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.3rem", marginTop: "0.75rem" }}>Member Login</h2>
            <p style={{ color: "var(--text-dim)", fontSize: "0.88rem", marginTop: "0.35rem" }}>Enter the password shared by your choir leader.</p>
          </div>
          <div>
            <input type="password" value={pw} onChange={e => { setPw(e.target.value); setErr(false); }} onKeyDown={e => e.key === "Enter" && login()} placeholder="Enter password"
              style={{ width: "100%", padding: "14px 16px", borderRadius: "var(--radius)", border: err ? "1px solid #e40303" : "1px solid rgba(255,255,255,0.12)", background: "var(--bg)", color: "var(--text)", fontFamily: "var(--font-body)", fontSize: "0.95rem" }} />
            {err && <p style={{ color: "#e40303", fontSize: "0.82rem", marginTop: "0.5rem" }}>That's not quite right — check with your choir leader.</p>}
            <button onClick={login} style={{ width: "100%", marginTop: "1rem", padding: "14px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "var(--radius)", fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Enter</button>
          </div>
          <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid rgba(255,255,255,0.07)", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Not a member? Just come along to a rehearsal — that's how you join.</p>
          </div>
        </Card>
      </div>
    </section>
  </div>);

  const tabs = [{ key: "resources", label: "Resources & Docs" }, { key: "membership", label: "Membership Form" }];
  return (<div>
    <section style={{ padding: "4rem 0 2rem", background: "radial-gradient(ellipse at 50% 0%, rgba(176,104,249,0.06) 0%, transparent 60%)" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
          <div><h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 700 }}>Members Area</h1><p style={{ color: "var(--text-dim)", marginTop: "0.4rem" }}>Welcome! Here's everything you need.</p></div>
          <button onClick={() => { setLoggedIn(false); setPw(""); }} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "8px 18px", color: "var(--text-muted)", fontSize: "0.82rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Log out</button>
        </div>
        <div style={{ display: "flex", gap: "0.25rem", marginTop: "2rem", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          {tabs.map(t => (<button key={t.key} onClick={() => setTab(t.key)} style={{ background: "none", border: "none", borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent", padding: "10px 20px", color: tab === t.key ? "var(--text)" : "var(--text-muted)", fontWeight: tab === t.key ? 600 : 400, fontSize: "0.9rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>{t.label}</button>))}
        </div>
      </div>
    </section>
    <section style={{ padding: "2rem 0 5rem" }}>
      <div className="container">
        {tab === "resources" && (<div style={{ maxWidth: "48rem" }}>
          <a href={CONFIG.GDRIVE_FOLDER} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "1.25rem", padding: "1.5rem 2rem", background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "var(--radius-lg)", transition: "all var(--transition)", marginBottom: "2rem", textDecoration: "none" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = ""; }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: subtleRainbow, border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>📁</div>
            <div><p style={{ fontWeight: 700, fontSize: "1.05rem" }}>Shared Google Drive</p><p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "0.1rem" }}>Sheet music, backing tracks, and shared files</p></div>
            <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: "1.2rem" }}>↗</span>
          </a>
          <SectionHead title="Documents" subtitle="Download key choir documents." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
            {MEMBER_DOCUMENTS.map((doc, i) => (<a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}><Card hover style={{ height: "100%" }}><span style={{ fontSize: "1.8rem" }}>{doc.icon}</span><h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginTop: "0.75rem" }}>{doc.name}</h4><p style={{ color: "var(--text-dim)", fontSize: "0.82rem", marginTop: "0.25rem" }}>{doc.desc}</p></Card></a>))}
          </div>
        </div>)}
        {tab === "membership" && (<div style={{ maxWidth: "44rem" }}>
          <p style={{ color: "var(--text-dim)", marginBottom: "1.5rem", fontSize: "0.92rem" }}>Fill in the membership form below. Your information is kept confidential in line with our Data Protection policy.</p>
          <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)", background: "#fff" }}>
            <iframe src={CONFIG.MEMBERSHIP_FORM} className="form-iframe" style={{ height: 1400 }} title="Membership form" />
          </div>
        </div>)}
      </div>
    </section>
  </div>);
}


/* ── CONTACT / NEWSLETTER PAGE ──────────────────────────────── */

function ContactPage() {
  const newsletter = useFormspree(CONFIG.FORMSPREE_NEWSLETTER);
  const contact = useFormspree(CONFIG.FORMSPREE_CONTACT);
  const [nlEmail, setNlEmail] = useState("");
  const [nlName, setNlName] = useState("");
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cMsg, setCMsg] = useState("");
  const [cNewsletter, setCNewsletter] = useState(false);

  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: "var(--radius)", border: "1px solid rgba(255,255,255,0.12)", background: "var(--bg)", color: "var(--text)", fontFamily: "var(--font-body)", fontSize: "0.92rem" };

  return (<div>
    <PageHeader title="Newsletter & Contact" subtitle="Stay in the loop or drop us a message." />

    <section style={{ padding: "2rem 0 4rem" }}>
      <div className="container" style={{ maxWidth: "52rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>

          {/* Newsletter */}
          <div>
            <SectionHead title="Join the newsletter" subtitle="No spam — just choir news and updates." />
            {newsletter.status === "sent" ? (
              <Card style={{ textAlign: "center", padding: "2.5rem" }}>
                <p style={{ fontSize: "1.5rem" }}>🎉</p>
                <p style={{ fontWeight: 700, marginTop: "0.5rem" }}>You're in!</p>
                <p style={{ color: "var(--text-dim)", fontSize: "0.9rem", marginTop: "0.3rem" }}>We'll be in touch with choir news and updates.</p>
                <button onClick={() => { newsletter.reset(); setNlName(""); setNlEmail(""); }} style={{ marginTop: "1rem", background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "6px 16px", color: "var(--text-dim)", fontSize: "0.82rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Sign up another</button>
              </Card>
            ) : (
              <Card>
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  <input type="text" placeholder="Your name" value={nlName} onChange={e => setNlName(e.target.value)} style={inputStyle} />
                  <input type="email" placeholder="Your email" value={nlEmail} onChange={e => setNlEmail(e.target.value)} style={inputStyle} />
                  <button onClick={() => newsletter.submit({ name: nlName, email: nlEmail, _subject: "New newsletter signup" })}
                    disabled={newsletter.status === "sending" || !nlEmail}
                    style={{ padding: "12px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "var(--radius)", fontWeight: 600, fontSize: "0.92rem", cursor: "pointer", fontFamily: "var(--font-body)", opacity: newsletter.status === "sending" || !nlEmail ? 0.6 : 1 }}>
                    {newsletter.status === "sending" ? "Sending..." : "Subscribe"}
                  </button>
                  {newsletter.status === "error" && <p style={{ color: "#e40303", fontSize: "0.82rem" }}>Something went wrong — try again.</p>}
                </div>
              </Card>
            )}
          </div>

          {/* Contact */}
          <div>
            <SectionHead title="Contact us" subtitle="Got a question? We'd love to hear from you." />
            {contact.status === "sent" ? (
              <Card style={{ textAlign: "center", padding: "2.5rem" }}>
                <p style={{ fontSize: "1.5rem" }}>✅</p>
                <p style={{ fontWeight: 700, marginTop: "0.5rem" }}>Message sent!</p>
                <p style={{ color: "var(--text-dim)", fontSize: "0.9rem", marginTop: "0.3rem" }}>We'll get back to you as soon as we can.</p>
                <button onClick={() => { contact.reset(); setCName(""); setCEmail(""); setCMsg(""); setCNewsletter(false); }} style={{ marginTop: "1rem", background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "6px 16px", color: "var(--text-dim)", fontSize: "0.82rem", cursor: "pointer", fontFamily: "var(--font-body)" }}>Send another</button>
              </Card>
            ) : (
              <Card>
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  <input type="text" placeholder="Your name" value={cName} onChange={e => setCName(e.target.value)} style={inputStyle} />
                  <input type="email" placeholder="Your email" value={cEmail} onChange={e => setCEmail(e.target.value)} style={inputStyle} />
                  <textarea placeholder="Your message" value={cMsg} onChange={e => setCMsg(e.target.value)} rows={5} style={{ ...inputStyle, resize: "vertical" }} />
                  <label style={{ display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer", fontSize: "0.88rem", color: "var(--text-dim)" }}>
                    <input type="checkbox" checked={cNewsletter} onChange={e => setCNewsletter(e.target.checked)} style={{ width: 18, height: 18, accentColor: "var(--accent)", cursor: "pointer" }} />
                    Also sign me up to the newsletter
                  </label>
                  <button onClick={() => contact.submit({ name: cName, email: cEmail, message: cMsg, newsletter_signup: cNewsletter ? "Yes" : "No", _subject: "Contact form: " + cName })}
                    disabled={contact.status === "sending" || !cEmail || !cMsg}
                    style={{ padding: "12px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "var(--radius)", fontWeight: 600, fontSize: "0.92rem", cursor: "pointer", fontFamily: "var(--font-body)", opacity: contact.status === "sending" || !cEmail || !cMsg ? 0.6 : 1 }}>
                    {contact.status === "sending" ? "Sending..." : "Send message"}
                  </button>
                  {contact.status === "error" && <p style={{ color: "#e40303", fontSize: "0.82rem" }}>Something went wrong — try again.</p>}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Other ways to reach us */}
        <div style={{ marginTop: "3rem" }}>
          <SectionHead title="Other ways to connect" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            <a href={`mailto:${CONFIG.EMAIL}`} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "1rem 1.5rem", background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "var(--radius-lg)", transition: "all var(--transition)" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"} onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}>
              <span style={{ fontSize: "1.3rem" }}>📧</span>
              <div><p style={{ fontWeight: 600, fontSize: "0.92rem" }}>Email</p><p style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>{CONFIG.EMAIL}</p></div>
            </a>
            <a href={CONFIG.FACEBOOK} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "1rem 1.5rem", background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "var(--radius-lg)", transition: "all var(--transition)" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"} onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}>
              <span style={{ fontSize: "1.3rem" }}><FacebookIcon size={22} /></span>
              <div><p style={{ fontWeight: 600, fontSize: "0.92rem" }}>Facebook</p><p style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>Croydon Inclusive Choir</p></div>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>);
}


/* ═══════════════════════════════════════════════════════════════
   10. APP
   ═══════════════════════════════════════════════════════════════ */

export default function App() {
  const [page, setPage] = useState("home");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [page]);

  const renderPage = () => {
    switch (page) {
      case "expect":  return <ExpectPage setPage={setPage} />;
      case "dates":   return <DatesPage />;
      case "gallery": return <GalleryPage />;
      case "members": return <MembersPage />;
      case "contact": return <ContactPage />;
      default:        return <HomePage setPage={setPage} />;
    }
  };

  return (<>
    <style>{globalCSS}</style>
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Nav page={page} setPage={setPage} />
      <main style={{ flex: 1 }}>{renderPage()}</main>
      <Footer setPage={setPage} />
    </div>
  </>);
}
